/**
 * Admin CloudWatch Logs Page
 *
 * Full-featured log viewer with:
 * - Log group selector (Lambda + API Gateway)
 * - Time range presets and custom range
 * - Filter pattern search (CloudWatch filter syntax)
 * - Live tail toggle (polls every 2s)
 * - Auto-scrolling log output with severity color-coding
 * - Load More pagination for historical logs
 *
 * Access: platform_admin only
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowDown,
  CaretDown as ChevronDown,
  Clock,
  Copy,
  FunnelSimple as Filter,
  Play,
  ArrowsClockwise as RefreshCw,
  MagnifyingGlass as Search,
  Stop,
  Terminal,
} from "@phosphor-icons/react";
import { cn } from "@/utils/cn";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { useAdminLogEvents, useAdminLogGroups } from "@/hooks/useAdmin";
import type { LogEvent } from "@/services/admin";

// ── Time range presets ─────────────────────────────────────

interface TimePreset {
  label: string;
  ms: number;
}

const TIME_PRESETS: TimePreset[] = [
  { label: "5m", ms: 5 * 60_000 },
  { label: "15m", ms: 15 * 60_000 },
  { label: "30m", ms: 30 * 60_000 },
  { label: "1h", ms: 60 * 60_000 },
  { label: "3h", ms: 3 * 60 * 60_000 },
  { label: "6h", ms: 6 * 60 * 60_000 },
  { label: "12h", ms: 12 * 60 * 60_000 },
  { label: "24h", ms: 24 * 60 * 60_000 },
];

const LIVE_TAIL_INTERVAL = 2000; // 2 seconds

// ── Log level detection ────────────────────────────────────

type LogLevel = "error" | "warn" | "info" | "debug" | "default";

function detectLogLevel(message: string): LogLevel {
  const lower = message.toLowerCase();
  if (
    lower.includes("error") ||
    lower.includes("exception") ||
    lower.includes("fatal")
  )
    return "error";
  if (lower.includes("warn")) return "warn";
  if (lower.includes("debug") || lower.includes("trace")) return "debug";
  if (lower.includes("info")) return "info";
  return "default";
}

const LEVEL_COLORS: Record<LogLevel, string> = {
  error: "text-red-400",
  warn: "text-yellow-400",
  info: "text-blue-400",
  debug: "text-zinc-500",
  default: "text-zinc-300",
};

// ── Helpers ────────────────────────────────────────────────

interface ParsedLogGroup {
  type: "lambda" | "apigw" | "other";
  method?: string;
  route: string;
  stack: string;
  label: string;
}

/**
 * Parse an SST-generated Lambda log group name into readable parts.
 * SST v2 convention: /aws/lambda/{stage}-{appName}-{StackAbbrev}-{CFLogicalId}-{hash}
 * e.g. /aws/lambda/prod-charisol-spark-ApiSt-ApiLambdaGETadminlogsgro-gNErxevNmXwH
 */
function parseLogGroupName(name: string): ParsedLogGroup {
  let type: "lambda" | "apigw" | "other" = "other";
  let remainder = name;

  if (name.startsWith("/aws/lambda/")) {
    type = "lambda";
    remainder = name.slice("/aws/lambda/".length);
  } else if (name.startsWith("/aws/vendedlogs/")) {
    type = "apigw";
    remainder = name.slice("/aws/vendedlogs/".length);
  }

  // Strip "{stage}-{appName}-" prefix, e.g. "prod-charisol-spark-"
  remainder = remainder.replace(/^[a-z]+-[a-z]+-[a-z]+-/, "");

  // Extract stack abbreviation (first hyphen-delimited segment)
  const dashIdx = remainder.indexOf("-");
  const stack = dashIdx !== -1 ? remainder.slice(0, dashIdx) : remainder;
  const logicalIdWithHash = dashIdx !== -1 ? remainder.slice(dashIdx + 1) : "";

  // Strip 12-char alphanumeric hash suffix
  const logicalId = logicalIdWithHash.replace(/-[A-Za-z0-9]{12}$/, "");

  // Parse "ApiLambda{METHOD}{routeLower}" CloudFormation logical ID pattern
  const routeMatch = logicalId.match(
    /^ApiLambda(GET|POST|PUT|PATCH|DELETE|OPTIONS|ANY)(.+)?$/i
  );
  if (routeMatch) {
    const method = routeMatch[1]!.toUpperCase();
    const routeRaw = (routeMatch[2] ?? "").toLowerCase();
    return {
      type,
      method,
      route: `/${routeRaw}`,
      stack,
      label: `${method} /${routeRaw}`,
    };
  }

  // Fallback: show cleaned logical ID
  return { type, route: logicalId || name, stack, label: logicalId || name };
}

function formatTimestamp(ts: number): string {
  return new Date(ts).toISOString().replace("T", " ").slice(0, 23);
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

// ── Component ──────────────────────────────────────────────

export function AdminLogsPage() {
  // State
  const [selectedGroup, setSelectedGroup] = useState("");
  const [timeRange, setTimeRange] = useState(TIME_PRESETS[3]!); // 1h default
  const [filterPattern, setFilterPattern] = useState("");
  const [appliedFilter, setAppliedFilter] = useState("");
  const [isLiveTail, setIsLiveTail] = useState(false);
  const [showGroupPicker, setShowGroupPicker] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [allEvents, setAllEvents] = useState<LogEvent[]>([]);

  const logContainerRef = useRef<HTMLDivElement>(null);
  const prevEventCountRef = useRef(0);
  const [refreshTick, setRefreshTick] = useState(0);

  // Stable time range — recomputes only when the user changes the preset or hits refresh.
  // Computing Date.now() directly each render would change the query key on every re-render
  // and cause an infinite fetch loop via the TanStack Query state → re-render cycle.
  const { startTime, endTime } = useMemo(
    () => ({ startTime: Date.now() - timeRange.ms, endTime: Date.now() }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [timeRange, refreshTick]
  );

  // Queries
  const { data: groupsData, isLoading: groupsLoading } = useAdminLogGroups();

  const {
    data: eventsData,
    isLoading: eventsLoading,
    isFetching: eventsFetching,
  } = useAdminLogEvents({
    logGroup: selectedGroup,
    startTime,
    endTime,
    filter: appliedFilter || undefined,
    limit: 200,
  });

  // Update allEvents when new data arrives
  useEffect(() => {
    if (eventsData?.events) {
      setAllEvents(eventsData.events);
    }
  }, [eventsData]);

  // Auto-scroll when new events arrive
  useEffect(() => {
    if (autoScroll && allEvents.length > prevEventCountRef.current) {
      logContainerRef.current?.scrollTo({
        top: logContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
    prevEventCountRef.current = allEvents.length;
  }, [allEvents.length, autoScroll]);

  // Live tail: advance the time window on each interval so new events are included
  useEffect(() => {
    if (!isLiveTail || !selectedGroup) return;
    const id = setInterval(
      () => setRefreshTick((t) => t + 1),
      LIVE_TAIL_INTERVAL
    );
    return () => clearInterval(id);
  }, [isLiveTail, selectedGroup]);

  // Handlers
  const handleGroupSelect = useCallback((name: string) => {
    setSelectedGroup(name);
    setShowGroupPicker(false);
    setAllEvents([]);
    setIsLiveTail(false);
  }, []);

  const handleApplyFilter = useCallback(() => {
    setAppliedFilter(filterPattern);
    setAllEvents([]);
  }, [filterPattern]);

  const handleTimeRangeChange = useCallback((preset: TimePreset) => {
    setTimeRange(preset);
    setAllEvents([]);
  }, []);

  const handleToggleLiveTail = useCallback(() => {
    setIsLiveTail((prev) => {
      if (!prev) setAutoScroll(true);
      return !prev;
    });
  }, []);

  const handleCopyLog = useCallback((message: string) => {
    navigator.clipboard.writeText(message);
  }, []);

  const groups = groupsData?.groups ?? [];

  return (
    <div className="flex h-[calc(100vh-7.5rem)] flex-col gap-4">
      {/* ── Header ── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight sm:text-2xl">
            CloudWatch Logs
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Browse and live-tail application logs.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {eventsFetching && !eventsLoading && (
            <span className="text-xs text-muted-foreground animate-pulse">
              Fetching…
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setRefreshTick((t) => t + 1)}
            disabled={!selectedGroup || eventsFetching}
          >
            <RefreshCw
              className={cn(
                "mr-1.5 h-3.5 w-3.5",
                eventsFetching && "animate-spin"
              )}
            />
            Refresh
          </Button>
        </div>
      </div>

      {/* ── Controls bar ── */}
      <div className="flex flex-col gap-3 rounded-xl border border-border/50 bg-card p-3 sm:flex-row sm:items-center">
        {/* Log group picker */}
        <div className="relative flex-1 min-w-0">
          <button
            onClick={() => setShowGroupPicker(!showGroupPicker)}
            className="flex w-full items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-sm transition-colors hover:bg-accent"
          >
            <Terminal className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate">
              {selectedGroup
                ? parseLogGroupName(selectedGroup).label
                : "Select log group…"}
            </span>
            <ChevronDown className="ml-auto h-4 w-4 shrink-0 text-muted-foreground" />
          </button>

          {showGroupPicker && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowGroupPicker(false)}
              />
              <div className="absolute left-0 top-full z-20 mt-1 max-h-80 w-full overflow-y-auto rounded-lg border border-border bg-card shadow-lg">
                {groupsLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <Spinner size="sm" />
                  </div>
                ) : groups.length === 0 ? (
                  <p className="px-3 py-4 text-center text-sm text-muted-foreground">
                    No log groups found.
                  </p>
                ) : (
                  groups.map((g) => {
                    const parsed = parseLogGroupName(g.name);
                    return (
                      <button
                        key={g.name}
                        onClick={() => handleGroupSelect(g.name)}
                        title={g.name}
                        className={cn(
                          "flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left text-sm transition-colors hover:bg-accent",
                          selectedGroup === g.name &&
                            "bg-primary/10 text-primary"
                        )}
                      >
                        <span className="flex min-w-0 flex-col">
                          <span className="truncate font-mono text-xs">
                            {parsed.method ? (
                              <>
                                <span className="font-semibold text-primary">
                                  {parsed.method}
                                </span>{" "}
                                {parsed.route}
                              </>
                            ) : (
                              parsed.label
                            )}
                          </span>
                          {parsed.stack && (
                            <span className="text-[10px] text-muted-foreground">
                              {parsed.stack}
                            </span>
                          )}
                        </span>
                        <span className="shrink-0 text-xs text-muted-foreground">
                          {formatBytes(g.storedBytes)}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>

        {/* Time range presets */}
        <div className="flex items-center gap-1">
          <Clock className="mr-1 h-3.5 w-3.5 text-muted-foreground" />
          {TIME_PRESETS.map((preset) => (
            <button
              key={preset.label}
              onClick={() => handleTimeRangeChange(preset)}
              className={cn(
                "rounded-md px-2 py-1 text-xs font-medium transition-colors",
                timeRange.label === preset.label
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground"
              )}
            >
              {preset.label}
            </button>
          ))}
        </div>

        {/* Filter pattern */}
        <div className="flex items-center gap-1.5">
          <div className="relative">
            <Filter className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={filterPattern}
              onChange={(e) => setFilterPattern(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleApplyFilter();
              }}
              placeholder="Filter pattern…"
              className="w-36 rounded-lg border border-border bg-background py-1.5 pl-8 pr-2 text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary sm:w-48"
            />
          </div>
          <Button variant="outline" size="sm" onClick={handleApplyFilter}>
            <Search className="h-3.5 w-3.5" />
          </Button>
        </div>

        {/* Live tail toggle */}
        <Button
          variant={isLiveTail ? "destructive" : "default"}
          size="sm"
          onClick={handleToggleLiveTail}
          disabled={!selectedGroup}
        >
          {isLiveTail ? (
            <>
              <Stop className="mr-1.5 h-3.5 w-3.5" />
              Stop
            </>
          ) : (
            <>
              <Play className="mr-1.5 h-3.5 w-3.5" />
              Live Tail
            </>
          )}
        </Button>
      </div>

      {/* ── Log viewer ── */}
      <div className="relative flex-1 overflow-hidden rounded-xl border border-border/50">
        {/* Status bar */}
        <div className="flex items-center justify-between border-b border-border bg-zinc-900 px-3 py-1.5">
          <div className="flex items-center gap-3 text-xs text-zinc-400">
            {selectedGroup && (
              <span className="font-mono">
                {parseLogGroupName(selectedGroup).label}
              </span>
            )}
            {allEvents.length > 0 && <span>{allEvents.length} events</span>}
            {isLiveTail && (
              <span className="flex items-center gap-1.5">
                <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-green-500" />
                Live
              </span>
            )}
          </div>
          <button
            onClick={() => setAutoScroll(!autoScroll)}
            className={cn(
              "flex items-center gap-1 rounded px-2 py-0.5 text-xs transition-colors",
              autoScroll
                ? "bg-primary/20 text-primary"
                : "text-zinc-500 hover:text-zinc-300"
            )}
          >
            <ArrowDown className="h-3 w-3" />
            Auto-scroll
          </button>
        </div>

        {/* Log output */}
        <div
          ref={logContainerRef}
          className="h-full overflow-y-auto bg-zinc-950 p-3 font-mono text-xs leading-relaxed"
          style={{ maxHeight: "calc(100% - 2rem)" }}
        >
          {!selectedGroup ? (
            <div className="flex h-full items-center justify-center text-zinc-600">
              <div className="text-center">
                <Terminal className="mx-auto mb-3 h-10 w-10" />
                <p>Select a log group to get started.</p>
              </div>
            </div>
          ) : eventsLoading ? (
            <div className="flex h-40 items-center justify-center">
              <Spinner size="md" />
            </div>
          ) : allEvents.length === 0 ? (
            <div className="flex h-full items-center justify-center text-zinc-600">
              <p>No log events found for the selected time range.</p>
            </div>
          ) : (
            <div className="space-y-0">
              {allEvents.map((event) => {
                const level = detectLogLevel(event.message);
                return (
                  <div
                    key={event.eventId}
                    className="group flex gap-3 rounded px-1 py-0.5 hover:bg-zinc-900/80"
                  >
                    <span className="shrink-0 text-zinc-600 select-none">
                      {formatTimestamp(event.timestamp)}
                    </span>
                    <pre
                      className={cn(
                        "flex-1 whitespace-pre-wrap break-all",
                        LEVEL_COLORS[level]
                      )}
                    >
                      {event.message}
                    </pre>
                    <button
                      onClick={() => handleCopyLog(event.message)}
                      className="shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
                      title="Copy log message"
                    >
                      <Copy className="h-3.5 w-3.5 text-zinc-600 hover:text-zinc-300" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
